import { gql, useLazyQuery } from '@apollo/client';
import { Query, SearchMetadataFieldsInput } from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';

export const SEARCH_METADATA_FIELD_GQL = gql`
  query SearchMetadataFields(
    $tenantID: ID!
    $query: String
    $excludeSources: [String!]
    $valueType: EQLFieldValueType
    $includeHidden: Boolean
    $key: String
  ) {
    SearchMetadataFields(
      request: {
        tenantID: $tenantID
        query: $query
        excludeSources: $excludeSources
        valueType: $valueType
        includeHidden: $includeHidden
        key: $key
      }
    ) {
      fields {
        field
        displayName
        iconURL
        objectType
        valueType
        isMultiValue
        iconURL
        detail {
          metadata {
            source
            references {
              key
              displayName
              source
              iconURL
            }
          }
          reference {
            creatorName
            referenceID
            referenceType
            creatorID
          }
        }
      }
    }
  }
`;

export const useSearchMetadataFields = () => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const [searchMetadataFields, { data, loading }] = useLazyQuery<
    Query,
    SearchMetadataFieldsInput
  >(SEARCH_METADATA_FIELD_GQL, {
    fetchPolicy: 'no-cache',
  });

  const queryMetadataFields = useCallback(
    ({
      query,
      excludeSources,
      valueType,
      key,
    }: Pick<
      SearchMetadataFieldsInput,
      'query' | 'excludeSources' | 'valueType' | 'key'
    >) => {
      searchMetadataFields({
        variables: {
          tenantID,
          query,
          key,
          excludeSources,
          valueType,
          includeHidden: true,
        },
      });
    },
    [searchMetadataFields, tenantID],
  );

  return [
    queryMetadataFields,
    {
      loading,
      searchMetadataFields:
        data?.SearchMetadataFields?.fields.map((field, index) => ({
          field: field.field,
          label: field.displayName ?? field.field,
          type: field.valueType,
          source: field.detail?.metadata?.source ?? 'Common',
          id: `${field.field}-${field.detail?.metadata?.source}`,
          sourceIconURL: field.iconURL,
        })) ?? [],
    },
  ] as const;
};
